import { EventCatalog, EventKeyValues } from "./catalog";
import { isExpectedErrorMessage } from "./expected-errors";
import stratumService from "./stratum-service";
import { EventType } from "./types";

/**
 * Build pluginData for Stratum.
 * - PostHog: receives all events (analytics, funnels, product usage).
 * - Rollbar: receives only error-type events (catalog eventType === ERROR), except
 *   expected validation messages (e.g. "email already exists") to keep Rollbar focused on actionable failures.
 */
function buildPluginData(
  eventKey: EventKeyValues,
  data: Record<string, unknown>,
) {
  const isErrorEvent = EventCatalog[eventKey]?.eventType === EventType.ERROR;
  const sendToRollbar = isErrorEvent && !isExpectedErrorMessage(data);

  return {
    PosthogPlugin: {
      eventType: "capture",
      properties: data,
    },
    ...(sendToRollbar && {
      RollbarPlugin: {
        properties: data,
      },
    }),
  };
}

/**
 * Build a payload that works for both PostHog $exception and Rollbar:
 * - Rollbar: needs `error` (Error instance) for stack traces.
 * - PostHog: needs serializable $exception_* fields (Error does not JSON-serialize well).
 */
function errorPayloadForBoth(
  errorObj: Error,
  extra: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...extra,
    // PostHog: serializable fields for $exception event
    $exception_message: errorObj.message,
    $exception_stack: errorObj.stack ?? undefined,
    $exception_type: errorObj.name,
    // Rollbar: Error instance for stack trace
    error: errorObj,
  };
}

export class EventPublisher {
  /**
   * Publish an event to all configured plugins (PostHog, Rollbar, etc.).
   * For error events: pass an Error in data.error so Rollbar gets the stack trace;
   * add $exception_message / $exception_type / $exception_stack for PostHog if desired.
   */
  static publishEvent(
    eventKey: EventKeyValues,
    data: Record<string, unknown> = {},
  ) {
    return stratumService
      .publish(eventKey, { pluginData: buildPluginData(eventKey, data) })
      .catch((error) => {
        // Never let observability failures break the app (Rollbar/PostHog down, etc.)
        if (process.env.NODE_ENV === "development") {
          console.error("Error publishing event:", error);
        }

        return Promise.resolve();
      });
  }

  /**
   * Publish an error event to both PostHog ($exception) and Rollbar with full context.
   * Use in catch blocks or when reporting failures. Sends one event to all plugins:
   * - Rollbar: receives the Error instance for stack traces.
   * - PostHog: receives $exception_message, $exception_type, $exception_stack for error tracking.
   */
  static publishError(
    eventKey: EventKeyValues,
    error: unknown,
    extra: Record<string, unknown> = {},
  ) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const data = errorPayloadForBoth(errorObj, extra);

    return EventPublisher.publishEvent(eventKey, data);
  }
}
